import React, { FC, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { CleanButton } from 'src/common/components/Button';
import { colors } from 'src/config/colors';
import RateUpIcon from 'src/assets/icons/RateUpIcon';
import { media } from 'src/config/breakpoints';
import { InfoRate } from 'src/common/components/InfoRate';
import { SpeakerInfo } from 'src/common/components/SpeakerInfo';
import { speakerFullName } from 'src/common/services/speaker';
import { Course } from 'src/common/models/course';
import StarEmptyIcon from 'src/assets/icons/StarEmptyIcon';
import { localFormatNumber } from 'src/lib/localFormatNumber';
import { getSpeakersItemPageRoute } from 'src/speakers/routes';
import Link from 'next/link';

const Container = styled.div`
  padding-top: 2.8125rem;

  ${media.w.min.px768(css`
    padding: 0 1.8125rem 0 1.5rem;
  `)};
`;

const Text = styled.div`
  grid-area: text;
  margin-top: 0.75rem;
  white-space: pre-line;

  ${media.w.min.px768(css`
    font-size: 0.625rem;
    line-height: 0.875rem;
  `)};
`;

const InfoContainer = styled.div`
  ${media.w.max.px768(css`
    display: flex;
    margin: 0 -0.25rem;
  `)};
`;

const TextButton = styled(CleanButton)`
  color: ${colors.pfp3bis};
  display: inline-block;
  font-size: 0.75rem;
  line-height: 1rem;
  margin-left: auto;
  margin-top: 0.5rem;
  text-decoration-line: underline;
`;

const TextContainer = styled.div`
  display: grid;
  grid-template-areas:
    'text'
    'button';

  ${media.w.min.px768(css`
    display: block;
  `)};
`;

export interface CourseSpeakerProps {
  course: Course;
}

export const CourseSpeaker: FC<CourseSpeakerProps> = ({
  course: { speakers, recommendationPercent },
}) => {
  const mainSpeaker = speakers?.[0]; // todo: FIX optionality!

  const bio = useMemo(() => (mainSpeaker ? mainSpeaker.bio.substring(0, 500) : ''), [mainSpeaker]);

  return (
    <Container>
      <InfoContainer>
        <InfoRate
          icon={RateUpIcon}
          rate={`${recommendationPercent}%`}
          text={`uczestników poleca szkolenia ${speakerFullName(mainSpeaker)}`}
        />
        {mainSpeaker?.averageRating && (
          <InfoRate
            icon={StarEmptyIcon}
            rate={localFormatNumber(mainSpeaker?.averageRating, {
              maximumFractionDigits: 2,
            })}
            text={`średnia ocena szkoleń ${speakerFullName(mainSpeaker)}`}
          />
        )}
      </InfoContainer>
      <SpeakerInfo
        avatarImg={mainSpeaker?.photo}
        speakerName={speakerFullName(mainSpeaker)}
        speakerCompany={mainSpeaker.company}
        linkTo={getSpeakersItemPageRoute(String(mainSpeaker.id))}
      />
      <TextContainer>
        <Text>{bio}</Text>
        <Link href={getSpeakersItemPageRoute(String(mainSpeaker.id))} passHref>
          <TextButton as="a">Czytaj więcej</TextButton>
        </Link>
      </TextContainer>
    </Container>
  );
};
